/**
 * Story 057 — Asset type Autocomplete (always rendered).
 *
 * Christian's Q6 lock (2026-05-13): the Asset type dropdown is always
 * rendered, no auto-hide-on-count=1 path. Sources from spec 062's
 * `assetTypes[]` rollup.
 */
import { Autocomplete, TextField } from "@mui/material";
import type { ReactElement } from "react";
import { useReadOnlyContext } from "@aeriosiq/ui";
import type { AssetTypeSummary } from "../queries/index.js";

export interface AssetTypeAutocompleteProps {
  value: string | null;
  options: readonly AssetTypeSummary[];
  onChange: (id: string | null) => void;
  disabled?: boolean;
  required?: boolean;
  loading?: boolean;
  /**
   * Inline error message shown beneath the field. Mirrors
   * `AssetModelAutocomplete.errorText` — used by the Create Project wizard's
   * holistic validation (FB-08) to flag a missing required asset type.
   */
  errorText?: string | null;
}

export function AssetTypeAutocomplete({
  value,
  options,
  onChange,
  disabled = false,
  required = false,
  loading = false,
  errorText = null,
}: AssetTypeAutocompleteProps): ReactElement {
  const selected = options.find((o) => o.id === value) ?? null;
  // SB2-CC-03 — forward read-only state from `<ReadOnlyModal>` context.
  const { readOnly } = useReadOnlyContext();
  return (
    <Autocomplete<AssetTypeSummary>
      options={[...options]}
      value={selected}
      disabled={disabled}
      readOnly={readOnly}
      loading={loading}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionLabel={(o) => o.name}
      onChange={(_e, next) => {
        onChange(next?.id ?? null);
      }}
      renderInput={(params) => {
        /* eslint-disable @typescript-eslint/no-explicit-any */
        const passThrough: any = params;
        return (
          <TextField
            {...passThrough}
            label="Asset type"
            required={required}
            error={typeof errorText === "string" && errorText.length > 0}
            {...(typeof errorText === "string" && errorText.length > 0
              ? { helperText: errorText }
              : {})}
            slotProps={{
              htmlInput: {
                ...params.inputProps,
                "aria-label": "Asset type",
                ...(required ? { "aria-required": true } : {}),
              },
            }}
            fullWidth
          />
        );
        /* eslint-enable */
      }}
    />
  );
}
